import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { CONTACT_SERVICE_OPTIONS } from '../config/contactServices';
import {
  isNonEmptyName,
  isValidEmail,
  isValidPhone,
  normalizeEmail,
} from '../utils/contactValidation';
import ContactPhoneInput from './ContactPhoneInput';
import ContactServiceSelect from './ContactServiceSelect';
import MaterialIcon from './MaterialIcon';

const API_URL = process.env.REACT_APP_CONTACT_API_URL || '/api/contact';

const SUCCESS_TOAST_MS = 10_000;
const TOAST_EXIT_MS = 420;

const emptyToast = { visible: false, variant: 'success', message: '' };

export default function ContactForm() {
  const formOpenedAtRef = useRef(Date.now());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(undefined);
  const [service, setService] = useState('');
  const [details, setDetails] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(emptyToast);

  const baseId = useId().replace(/:/g, '');

  const showToast = useCallback((message, variant = 'success') => {
    setToast({ visible: true, variant, message });
  }, []);

  useEffect(() => {
    if (!toast.visible || !toast.message) return undefined;
    const hide = setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
    }, SUCCESS_TOAST_MS);
    return () => clearTimeout(hide);
  }, [toast.visible, toast.message]);

  useEffect(() => {
    if (toast.visible || !toast.message) return undefined;
    const clear = setTimeout(() => setToast(emptyToast), TOAST_EXIT_MS);
    return () => clearTimeout(clear);
  }, [toast.visible, toast.message]);

  const validate = () => {
    const next = {};
    if (!isNonEmptyName(name)) next.name = 'Enter your name or company (at least 2 characters).';
    if (!isValidEmail(email)) next.email = 'Enter a valid email address.';
    if (!isValidPhone(phone)) next.phone = 'Enter a valid phone number with country code.';
    if (!service) next.service = 'Choose the service you’re interested in.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    if (!validate()) return;

    setSubmitting(true);
    setErrors({});

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: normalizeEmail(email),
          phone: String(phone ?? '').trim(),
          service,
          details: details.trim(),
          website: honeypot,
          formOpenedAt: formOpenedAtRef.current,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        setName('');
        setEmail('');
        setPhone(undefined);
        setService('');
        setDetails('');
        formOpenedAtRef.current = Date.now();
        showToast(
          "We've received your message and will reach out using the email or phone you provided. Thank you for contacting CrimsonArc Technologies.",
          'success'
        );
      } else {
        showToast(data.error || 'Something went wrong. Please try again or email us directly.', 'error');
      }
    } catch {
      showToast('Network error. Check your connection or email hello@crimsonarctechnologies.online.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form className="ca-contact-form" onSubmit={onSubmit} noValidate>
        <p className="ca-contact-form__title">Send us a message</p>
        <p className="ca-contact-form__subtitle">
          Tell us what you need—we’ll reply through your email or phone.
        </p>

        <div className="ca-contact-form__hp" aria-hidden="true">
          <label htmlFor={`${baseId}-website`}>Company website</label>
          <input
            id={`${baseId}-website`}
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(ev) => setHoneypot(ev.target.value)}
          />
        </div>

        <div className="ca-contact-form__field">
          <label htmlFor={`${baseId}-name`}>Name or company</label>
          <input
            id={`${baseId}-name`}
            name="name"
            type="text"
            required
            autoComplete="organization"
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            className={errors.name ? 'ca-contact-form__input ca-contact-form__input--error' : 'ca-contact-form__input'}
            maxLength={200}
          />
          {errors.name ? <span className="ca-contact-form__err">{errors.name}</span> : null}
        </div>

        <div className="ca-contact-form__row">
          <div className="ca-contact-form__field">
            <label htmlFor={`${baseId}-email`}>Email</label>
            <input
              id={`${baseId}-email`}
              name="email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              className={errors.email ? 'ca-contact-form__input ca-contact-form__input--error' : 'ca-contact-form__input'}
              maxLength={254}
            />
            {errors.email ? <span className="ca-contact-form__err">{errors.email}</span> : null}
          </div>
          <div className="ca-contact-form__field">
            <label htmlFor={`${baseId}-phone`}>Phone number</label>
            <ContactPhoneInput
              id={`${baseId}-phone`}
              value={phone}
              onChange={setPhone}
              error={Boolean(errors.phone)}
              ariaDescribedBy={`${baseId}-phone-err`}
            />
            {errors.phone ? (
              <span id={`${baseId}-phone-err`} className="ca-contact-form__err" role="alert">
                {errors.phone}
              </span>
            ) : null}
          </div>
        </div>

        <div className="ca-contact-form__field ca-contact-form__field--mui-select">
          <ContactServiceSelect
            id={`${baseId}-service`}
            labelId={`${baseId}-service-label`}
            value={service}
            onChange={setService}
            error={Boolean(errors.service)}
            helperText={errors.service}
            options={CONTACT_SERVICE_OPTIONS}
          />
        </div>

        <div className="ca-contact-form__field">
          <label htmlFor={`${baseId}-details`}>
            Project details <span className="ca-contact-form__optional">(optional)</span>
          </label>
          <textarea
            id={`${baseId}-details`}
            name="details"
            rows={3}
            value={details}
            onChange={(ev) => setDetails(ev.target.value)}
            className="ca-contact-form__textarea"
            maxLength={2000}
            placeholder="Goals, timeline, stack, or anything that helps us prepare."
          />
        </div>

        <button type="submit" className="ca-btn ca-btn--primary ca-contact-form__submit" disabled={submitting}>
          {submitting ? (
            <>
              <MaterialIcon name="hourglass_empty" variant="none" className="ca-contact-form__submit-icon" />
              Sending…
            </>
          ) : (
            <>
              <MaterialIcon name="send" variant="none" className="ca-contact-form__submit-icon" />
              Submit
            </>
          )}
        </button>
      </form>

      {toast.message ? (
        <div
          className={`ca-toast ca-toast--${toast.variant} ${toast.visible ? 'ca-toast--visible' : ''}`}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="ca-toast__icon" aria-hidden>
            {toast.variant === 'success' ? '✓' : '!'}
          </span>
          <p className="ca-toast__text">{toast.message}</p>
        </div>
      ) : null}
    </>
  );
}
